// Countertops/Cabinets detection + routing helpers.
//
// The domain has no "division"/"workflow" entity. The canonical axis identified in
// Phase 1 is the STORE: a Countertops/Cabinets store has `storeTypeId === 2`
// (see the store filtering in `admin/kits/List.jsx`), and `GetStore` also returns an
// `isCountertopCabinet` flag. A customer record carries only `storeId`, so detection
// resolves the customer's `storeId` against the store list (`StoreInfo.storeInfo`).
//
// NEVER detect by store name (e.g. "Arizona Countertops and Cabinets") — that is a
// test store only and must not be hard-coded.

import {
  CARPET_ID_DB,
  SHEET_VINYL_ID_DB,
  VINYL_PLANK_ID_DB,
} from "./constants/ProductCatalog";

// Store type id that marks a Countertops/Cabinets branch/store.
export const COUNTERTOP_CABINET_STORE_TYPE_ID = 2;

// The "primary" flooring master products shown in the initial Pricing Contract step for a
// normal (non-C/C) customer. These ids come from the product catalog constants (DB ids),
// not from literals inline in the component.
export const PRIMARY_FLOORING_STRUCTURE_IDS = [
  CARPET_ID_DB,
  SHEET_VINYL_ID_DB,
  VINYL_PLANK_ID_DB,
];

// Base path segment for the Countertops/Cabinets manual quantity calculator.
export const COUNTERTOP_CABINET_QUANTITY_BASE =
  "/customer-list/customer-detail/countertop-cabinet-quantity";

// Find a store record by id within a store list (StoreInfo.storeInfo).
// Ids are compared numerically because `<select>` values arrive as strings.
export function findStoreById(storeId, storeList = []) {
  if (storeId === null || storeId === undefined || storeId === "") return null;
  if (!Array.isArray(storeList)) return null;
  const target = Number(storeId);
  if (Number.isNaN(target)) return null;
  return storeList.find((store) => Number(store?.storeId) === target) || null;
}

// True when the given store belongs to the Countertops/Cabinets workflow.
// Unknown / missing store => false, so callers fall back to existing behavior.
export function isCountertopCabinetStore(storeId, storeList = []) {
  const store = findStoreById(storeId, storeList);
  if (!store) return false;
  return (
    Number(store.storeTypeId) === COUNTERTOP_CABINET_STORE_TYPE_ID ||
    store.isCountertopCabinet === true
  );
}

// Build the in-app route for the Countertops/Cabinets manual quantity calculator.
// Preserves the identifying route params (customer + floor plan / variant).
// Returns null when a required identifier is missing so callers keep existing behavior.
export function buildCountertopCabinetQuantityPath(contactInfoId, floorPlan = {}) {
  const isBlank = (v) => v === null || v === undefined || v === "";
  const unitTypeId = floorPlan?.unitTypeId;
  if (isBlank(contactInfoId) || isBlank(unitTypeId)) return null;

  const base = `${COUNTERTOP_CABINET_QUANTITY_BASE}/${contactInfoId}/${unitTypeId}`;
  const variantId = floorPlan?.variantId;
  return isBlank(variantId) ? base : `${base}/${variantId}`;
}

// Select which master products (product structures) the Pricing Contract material-selection
// step should display, given the customer's division and the step mode.
//
// - `valueList`         : ProductType.valueList (the master-product / product-structure catalog)
// - `isCountertopCabinet`: the customer's division flag (from store detection above)
// - `mode`             : 'primary' (initial contract create) | 'add' (add-material modal)
//
// Countertops/Cabinets customers get ALL master products flagged `isCountertopCabinet` in the
// database — selection is driven by the DB flag, NOT by hard-coded product ids. The `mode`
// param is ignored for C/C (same list shown in both the initial step and add-material modal).
// Normal customers keep the exact existing behavior.
export function selectPricingContractMasterProducts(
  valueList = [],
  { isCountertopCabinet = false, mode = "primary" } = {}
) {
  if (!Array.isArray(valueList)) return [];

  if (isCountertopCabinet) {
    return valueList.filter((item) => item?.isCountertopCabinet === true);
  }

  // Existing (flooring) behavior:
  if (mode === "add") {
    // "Add material" modal: everything that is not a Countertops/Cabinets structure.
    return valueList.filter((item) => !item?.isCountertopCabinet);
  }
  // Initial "What would you like to price?" step: the primary flooring structures only.
  return valueList.filter((item) =>
    PRIMARY_FLOORING_STRUCTURE_IDS.includes(item?.productStructureId)
  );
}
